import { Viewer } from "../main";
import {
  SceneLabelTools,
  AerialLabelTools,
  textToColorArray
} from ".";
import { nanoid } from "@reduxjs/toolkit";
import LocalScene, { isValidProjection } from "../projections";
import {Line3, ShapeUtils, Vector2, Vector3} from "three";
import {
  polygon as turfPolygon,
  lineString as turfLineString,
  point as turfPoint,
  simplify as turfSimplify,
  union as turfUnion
} from '@turf/turf';
import { PanoramicImage, PlanarImage } from "../cameras";
import * as jsts from 'jsts/dist/jsts';
import { toast } from "../../../app";
import { imagePixelsToAerial } from "../aerial";
import { coordinatesToArray } from "../utilities";
import { clearAerialTooltip, clearViewerTooltip } from "../../viewer";

export interface CocoCategory {
  supercategory: string,
  id: number,
  name: string,
  color: [number, number, number]
}

export interface CocoImage {
  height: number,
  width: number,
  id: number,
  file_name: string,
  metadata?: {
    x_skew: number,
    y_skew: number,
    x_scale: number,
    y_scale: number,
    x_offset: number,
    y_offset: number
  }
}

export interface CocoAnnotation {
  segmentation: Array<[]>,
  iscrowd: number,
  image_id: number,
  merge_id?: number
  bbox: [number, number, number, number],
  category_id: number,
  id: number,
  area: number,
  score: number,
  text: string,
  textScore: number,
  findText: number
}

export class ImageLabel {
  public id;
  public category;
  public selected = false;
  public completed = false;
  public editing = false;
  public valid = true;
  public drawable = true;
  public highlighted = false;
  public score = 1.0;
  public text = '';
  public textScore = 1.0;
  public findText = false;
  private duplicatePixelDistance = 5;
  public coordinates = [];

  constructor() {
    this.id = nanoid();
  }

  get hasEnoughPoints() {
    console.warn("Not Implemented");
    return false;
  }

  get numPoints() {
    console.warn("Not Implemented");
    return 0;
  }

  select() {
    const stateChanged = !this.selected;
    this.selected = true;
    this.highlighted = false;
    this.completed = false;

    if (stateChanged) {
      this.draw();
    }
  }

  unSelect() {
    this.selected = false;
    this.complete();
  }

  complete() {
    const stateChanged = !this.completed;
    this.completed = true;

    if (stateChanged) {
      this.draw();
    }
  }

  setDrawable(state) {
    this.drawable = state;
  }

  setEditing(state) {
    this.editing = state;
  }

  setScore(score) {
    if (score){
      this.score = score;
    } else {
      this.score = 1.0;
    }
  }

  setText(text, score=1.0) {
    if (typeof text === 'string') {
      this.text = text;
    } else {
      this.text = '';
    }
    this.textScore = score;
  }

  setFindText(num) {
    this.findText = num;
  }

  zoom(): Promise<boolean> {
    console.warn("Not Implemented");
    return Promise.resolve(false);
  }

  setHighlighted(highlight) {
    console.warn("Not Implemented");
  }

  destroy() {
    this.clear();
  }

  clear() {
    console.warn("Not Implemented");
  }

  draw() {
    console.warn("Not Implemented");
  }

  insertPoint(point, index) {
    console.warn("Not Implemented");
  }

  createCoordData(coordinate) {
    console.warn("Not Implemented");

    let coordArray = [];
    let point = new Vector3();

    return {coordArray, point};
  }

  pointToScreen(point) {
    console.warn("Not Implemented");
    return [0, 0];
  }

  duplicatePoint(oldPoint, newPoint) {
    if (!oldPoint) {
      return false;
    }

    // Check screen distance from previous point
    let screenPosNew = this.pointToScreen(oldPoint);
    let screenPosOld = this.pointToScreen(newPoint);
    let dU = screenPosNew[0] - screenPosOld[0];
    let dV = screenPosNew[1] - screenPosOld[1];
    let distance = Math.sqrt(dU**2 + dV**2);

    return distance < this.duplicatePixelDistance;
  }
}

export class LabelCategory {
  public id;
  public name;
  public listIdx = -1;
  public labels: ImageLabel[] = [];
  private invalidLabels: ImageLabel[] = [];
  private sorted = false;
  private originalOrder = [];

  constructor(name) {
    this.id = nanoid();
    this.name = name;
  }

  get numLabelsAll() {
    return this.labels.length + this.invalidLabels.length;
  }

  getLabelByID(labelID) {
    return this.labels.find(label => label.id === labelID);
  }

  setText(labelID, text, score=1.0) {
    let label = this.getLabelByID(labelID);
    if (label) {
      label.setText(text, score);
    }
    return;
  }

  setFindText(state) {
    this.labels.forEach(label => label.findText = state);
  }

  addNewLabel(drawLabel) {
    console.warn("Not implemented");
    return null;
  }

  updateColor() {
    console.warn("Not implemented");
  }

  featuresLayer() {
    console.warn("Not implemented");
  }

  insertPoint(labelID, coordinate) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    let {coordArray, point} = label.createCoordData(coordinate);
    let idxCoord = minDistanceIdx(coordArray, point);
    label.insertPoint(point, idxCoord);

    toast.success("Added point");
  }

  editLabel(labelID, coordinate) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    label.setEditing(true);

    let {coordArray, point} = label.createCoordData(coordinate);
    let idxCoord = minDistanceIdx(coordArray, point);
    let endIdx = (idxCoord === coordArray.length - 1) ? 0 : idxCoord + 1;
    let line = createSegment(coordArray[idxCoord], coordArray[endIdx]);

    let reverse = line.closestPointToPointParameter(point, true) > 0.5;
    label.coordinates = [
      ...label.coordinates.slice(idxCoord+1, label.coordinates.length),
      ...label.coordinates.slice(0, idxCoord+1)
    ];

    if (reverse) {
      label.coordinates.reverse();
    }
  }

  deleteLabel(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    label.destroy();
    const index = this.labels.indexOf(label);
    this.labels.splice(index, 1);
  }

  deleteLabels(labelIDs) {
    labelIDs.forEach(labelID => {
      this.deleteLabel(labelID);
    });
  }

  async zoomToLabel(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    let validLabel = await label.zoom();
    if (validLabel) {
      label.setHighlighted(true);
    }
  }

  unHighlight(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    label.setHighlighted(false);
  }

  updateName(name) {
    this.name = name;
    this.updateColor();
  }

  completeLabel(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return false;
    }

    if (!label.hasEnoughPoints) {
      return false;
    }

    label.setHighlighted(false);
    label.unSelect();
    label.complete();

    return true;
  }

  unSelectAll() {
    this.labels.forEach(label =>
      label.unSelect()
    );
  }

  selectLabel(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    label.select();
    label.score = 1.0;
  }

  sortLabels(scoreThreshold, type) {
    let allLabels = [...this.labels, ...this.invalidLabels];

    if (!this.sorted) {
      let order = [];
      this.labels.forEach(label => order.push(label.id));
      this.originalOrder = order;
      this.sorted = true;
    }

    if (type === "ascending"){
      allLabels.sort((a,b) => a.score - b.score);
    }
    else if (type === "descending") {
      allLabels.sort((a,b) => b.score - a.score);
    }

    this.labels = allLabels.filter(label => label.score > scoreThreshold);
    this.invalidLabels = allLabels.filter(label => label.score <= scoreThreshold);

    this.invalidLabels.forEach(label => label.clear());
  }

  undoSort() {
    let allLabels = [...this.labels, ...this.invalidLabels];

    let unsortedLabels = [];
    this.originalOrder.forEach(labelID => {
      let match = allLabels.find(label => label.id === labelID);
      unsortedLabels.push(match);
    });

    this.labels = unsortedLabels;
    this.invalidLabels = [];
    this.sorted = false;
  }

  moveToLabelInList(labelID) {
    this.labels.forEach((label, index) => {
      if (label.id === labelID) {
        this.listIdx = index;
      }
    });
  }
}

export class ImageLabelTools {
  viewer: Viewer;
  public orthoLabels: AerialLabelTools;
  public sceneLabels: SceneLabelTools;
  private activeLabeler: AerialLabelTools | SceneLabelTools;
  public categories: LabelCategory[] = [];
  private setCategoryList: Function;
  private setTrainingArea: Function;
  private currentCategory;
  private allowUpdate = true;

  constructor(viewer: Viewer, props) {
    const {setCategoryList, setTrainingArea} = props;

    this.viewer = viewer;
    this.orthoLabels = new AerialLabelTools(this, props);
    this.sceneLabels = new SceneLabelTools(this);
    this.setCategoryList = setCategoryList;
    this.setTrainingArea = setTrainingArea;

    this.setCategories();
  }

  get currentCategoryName() {
    return this.currentCategory.name;
  }

  get trainingArea() {
    if (this.activeLabeler instanceof AerialLabelTools) {
      return this.activeLabeler.trainingArea;
    }

    return null;
  }

  get numHighlighted() {
    let count = 0;
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.highlighted) {
          count++;
        }
      });
    });

    this.trainingArea?.labels.forEach(label => {
      if (label.highlighted) {
        count++;
      }
    });

    return count;
  }

  get noneHighlighted() {
    return this.numHighlighted === 0;
  }

  get oneHighlighted() {
    return this.numHighlighted === 1;
  }

  getIdxInList(category) {
    let validCategory = this.categories.find(x => x.name === category.name);
    if (!validCategory) {
      return;
    }

    return validCategory.listIdx;
  }

  setUpdateState(state) {
    this.allowUpdate = state;
  }

  setFindText(categoryID) {
    this.categories.forEach(category => category.setFindText(false));

    const category = this.getCategoryByID(categoryID);
    if (!category) {
      return;
    }

    category.setFindText(true);
  }

  onKeyDown(event) {
    let label = this.activeLabeler.activeLabel;

    if (label) {
      // Check for completion hotkey -> Enter
      if (event.which === 13) {
        if (label.selected){
          this.completeLabel(label.id);
        }
      }

      // Check for deletion hotkey -> Delete
      if (event.which === 46) {
        this.activeLabeler.deletePoint();
      }

    }
    else {
      // Check add new label in current category hotkey -> space
      if (event.which === 32 && (this.currentCategory)) {
        this.addLabel(this.currentCategory.id);
        return;
      }
    }

  }

  setActiveLabelType(type) {
    this.deleteAllCategories();
    this.orthoLabels.setState(false);
    this.sceneLabels.setState(false);
    this.viewer.minimap.setDragState(true);

    if (type === "ortho") {
      this.activeLabeler = this.orthoLabels;
    } else if (type === "scene") {
      this.activeLabeler = this.sceneLabels;
    } else {
      this.activeLabeler = null;
      return;
    }

    this.activeLabeler.setState(true);
  }

  activeImagePaths() {
    if (!this.activeLabeler) {
      return [];
    }

    return this.activeLabeler.activeImagePaths;
  }

  activeImages() {
    if (!this.activeLabeler) {
      return [];
    }

    return this.activeLabeler.activeImages;
  }

  loadedImagePaths() {
    if (!this.activeLabeler) {
      return [];
    }

    return this.activeLabeler.loadedImagePaths;
  }

  checkIfSelected() {
    if (!this.activeLabeler) {
      return false;
    }

    return this.activeLabeler.activeLabel
      ? true
      : false;
  }

  checkUniqueName(name) {
    const categoriesWithName = this.categories.filter(category => {
      return category.name.toUpperCase() === name.toUpperCase();
    });

    return (categoriesWithName.length === 0);
  }

  getCategoryByID(id) {
    if (this.trainingArea?.id === id) {
      return this.trainingArea;
    }

    return this.categories.find(x => x.id === id);
  }

  setCategories() {
    if (!this.allowUpdate) {
      return;
    }

    // Sort categories based on name
    //this.categories.sort((a, b) => a.name.localeCompare(b.name));

    this.setCategoryList([...this.categories]);

    if (this.trainingArea) {
      this.setTrainingArea([this.trainingArea]);
    } else {
      this.setTrainingArea([]);
    }
  }

  sortCategory(category, score, sortType) {
    if (!this.activeLabeler) {
      return;
    }

    let categoryToSort = this.categories.find(x => x.name === category.name);
    if (!categoryToSort) {
      return;
    }

    this.unHighlightAll();
    categoryToSort.unSelectAll();

    categoryToSort.sortLabels(score, sortType);
    this.setCategories();
    this.activeLabeler.drawAll();
  }

  undoSort(category) {
    if (!this.activeLabeler) {
      return;
    }

    let categoryToSort = this.categories.find(x => x.name === category.name);
    if (!categoryToSort) {
      return;
    }

    this.unHighlightAll();
    categoryToSort.unSelectAll();

    categoryToSort.undoSort();
    this.setCategories();
    this.activeLabeler.drawAll();
  }

  addCategory(name) {
    if (!this.activeLabeler) {
      return;
    }

    let category = this.activeLabeler.getNewCategory(name);
    this.categories.push(category);

    if (this.categories.length === 1) {
      this.currentCategory = category;
    }

    this.setCategories();

    return category;
  }

  addTrainingArea() {
    if (this.activeLabeler instanceof SceneLabelTools) {
      return;
    }

    if (this.trainingArea) {
      return;
    }

    let trainingArea = this.activeLabeler.getNewTrainingArea();
    this.activeLabeler.setTrainingArea(trainingArea);
    this.setCategories();

    return trainingArea;
  }

  deleteCategory(categoryID) {
    const category = this.getCategoryByID(categoryID);
    if (!category) {
      return;
    }

    category.labels.forEach(label => {
      label.destroy();
    });

    const index = this.categories.indexOf(category);
    this.categories.splice(index, 1);

    if (this.currentCategory === category) {
      this.currentCategory = this.categories[0];
    } else if (this.categories.length === 0) {
      this.currentCategory = null;
    }

    this.setCategories();
  }

  editCategoryName(categoryID, name) {
    const category = this.getCategoryByID(categoryID);
    if (!category) {
      return;
    }

    category.updateName(name);
    this.setCategories();
  }

  deleteAllCategories() {
    let categories = [...this.categories];
    categories.forEach(category => {
      this.deleteCategory(category.id);
    });

    if (this.trainingArea) {
      this.deleteTrainingArea();
    }
  }

  deleteTrainingArea() {
    if (this.activeLabeler instanceof SceneLabelTools) {
      return;
    }

    this.activeLabeler.deleteTrainingArea();
    this.setCategories();
  }

  addTrainingAreaLabel(drawLabel=true) {
    if (this.activeLabeler instanceof SceneLabelTools) {
      return;
    }

    if (!this.trainingArea) {
      return;
    }

    const trainingArea = this.trainingArea;
    this.currentCategory = trainingArea;

    let label = trainingArea.addNewLabel(drawLabel);
    this.unSelectLabels();
    trainingArea.selectLabel(label.id);

    this.setCategories();

    return label;
  }

  addLabel(categoryID, drawLabel=true) {
    const category = this.getCategoryByID(categoryID);
    if (!category) {
      return;
    }
    this.currentCategory = category;

    let label = category.addNewLabel(drawLabel);
    this.unSelectLabels();
    category.selectLabel(label.id);

    this.setCategories();

    return label;
  }

  editLabel(labelID, coord) {
    this.unHighlight(labelID);
    this.selectLabel(labelID);
    this.categories.forEach(category => {
      category.editLabel(labelID, coord);
    });
  }

  deleteLabel(labelID) {
    this.categories.forEach(category => {
      category.deleteLabel(labelID);
    });

    this.trainingArea?.deleteLabel(labelID);

    this.setCategories();
    this.clearTooltips();
  }

  deletePoint(pointID) {
    this.activeLabeler.deleteSpecificPoint(pointID);
  }

  insertPoint(labelID, coord) {
    this.unHighlight(labelID);
    this.categories.forEach(category => {
      category.insertPoint(labelID, coord);
    });
  }

  setText(labelID, text, score=1.0) {
    this.categories.forEach(category => {
      category.setText(labelID, text, score);
    });
  }

  async zoomToLabel(labelID) {
    this.unHighlightAll();

    for (const category of this.categories) {
      await category.zoomToLabel(labelID);
    };

    await this.trainingArea?.zoomToLabel(labelID);
    this.setCategories();
  }

  unHighlight(labelID) {
    this.categories.forEach(category => {
      category.unHighlight(labelID);
    });

    this.trainingArea?.unHighlight(labelID);
    this.setCategories();
  }

  unHighlightAll() {
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.highlighted) {
          category.unHighlight(label.id);
        }
      });
    });

    this.trainingArea?.labels.forEach(label => {
      if (label.highlighted) {
        this.trainingArea.unHighlight(label.id);
      }
    });

    this.setCategories();
  }

  deleteHighlighted() {
    this.categories.forEach(category => {
      let labelIDs = [];
      category.labels.forEach(label => {
        if (label.highlighted) {
          labelIDs.push(label.id);
        }
      });
      category.deleteLabels(labelIDs);
    });

    let labelIDs = [];
    this.trainingArea?.labels.forEach(label => {
      if (label.highlighted) {
        labelIDs.push(label.id);
      }
    });
    this.trainingArea?.deleteLabels(labelIDs);

    this.setCategories();
  }

  completeLabel(labelID) {
    let success = false;
    let label = this.activeLabeler.activeLabel;

    this.categories.forEach(category => {
      success = success || category.completeLabel(labelID);
    });

    const isTrainingArea = this.trainingArea
      ? this.trainingArea.completeLabel(labelID)
      : false;

    success = success || isTrainingArea;

    this.setCategories();
    this.clearTooltips();

    if (!success || !label) {
      return;
    }

    let message;

    if (isTrainingArea) {
      message = "Added training area";
    } else {
      message = label.editing
        ? "Updated label"
        : "Added label";
    }

    toast.success(message);
    label.setEditing(false);
  }

  selectLabel(labelID) {
    this.unSelectLabels();
    this.categories.forEach(category => {
      category.unHighlight(labelID);
      category.selectLabel(labelID);
    });

    this.trainingArea?.unHighlight(labelID);
    this.trainingArea?.selectLabel(labelID);

    this.activeLabeler.resetTemporaryPoint();
    this.setCategories();
  }

  unSelectLabels() {
    this.categories.forEach(category => {
      category.unSelectAll();
    });

    this.trainingArea?.unSelectAll();
  }

  moveToLabelInList(labelID) {
    this.categories.forEach(category => {
      category.moveToLabelInList(labelID);
    });

    this.setCategories();
  }

  simplifyPolygon(points, tolerance=2, highQuality=false) {
    let simplePolygon;
    let newPoints = [...points, points[0]];
    let polygon = turfPolygon([newPoints]);
    let options = {tolerance, highQuality};

    try {
      let simplified = turfSimplify(polygon, options);
      simplePolygon = simplified.geometry.coordinates[0];
      simplePolygon.pop();
    } catch {
      simplePolygon = [];
    }

    // console.log(`Simplify geometry: ${newPoints.length} => ${simplePolygon.length}`);

    return simplePolygon;
  }

  mergeTrainingLabels(category, labelsToMerge) {
    let maxPointDistance = 0.5;
    let mergedLabels = new Set();

    labelsToMerge.forEach((firstLabel, index) => {
      if (mergedLabels.has(firstLabel.id)) {
        return;
      }

      const isPlanarImage = firstLabel.camera instanceof PlanarImage;
      const isPanoramicImage = firstLabel.camera instanceof PanoramicImage;

      // Not required for planar images
      if (isPlanarImage) {
        return;
      }

      let mergeableArray = labelsToMerge.slice(index + 1);
      mergeableArray.forEach(mergeLabel => {
        if (mergedLabels.has(mergeLabel.id)) {
          return;
        }

        if (firstLabel.camera !== mergeLabel.camera) {
          return;
        }

        let points1 = [...firstLabel.coords];
        let points2 = [...mergeLabel.coords];

        if (isPanoramicImage) {
          // Add image offset
          let width = firstLabel.imageWidth;
          points1 = points1.map(x => [(x[0]+(width/2)) % width, x[1]]);
          points2 = points2.map(x => [(x[0]+(width/2)) % width, x[1]]);
        }

        let matches = [];
        points1.forEach((point1, index1) => {
          let matchFound = false;
          points2.forEach((point2, index2) => {
            if (matchFound) {
              return;
            }

            let dist = new Vector2(...point1)
              .distanceTo(new Vector2(...point2));

            if (dist < maxPointDistance) {
              matchFound = true;
              matches.push({point1, point2, dist, index1, index2});
            }
          });
        });

        if (matches.length === 0) {
          return;
        }

        // Take the two best matches and update
        // the original point to match the second
        matches.sort((a,b) => a.dist - b.dist);
        matches = matches.slice(0,2);
        matches.forEach(data => {
          points1[data.index1] = data.point2;
        });

        let polygonPoints1 = [...points1, points1[0]];
        let polygonPoints2 = [...points2, points2[0]];

        let union = turfUnion(
          turfPolygon([polygonPoints1]),
          turfPolygon([polygonPoints2])
        );

        if (!union) {
          return;
        }

        // Union must have a single geometry
        if (union.geometry.type !== "Polygon") {
          return;
        }

        let coordinates = union.geometry.coordinates[0] as any;
        coordinates.pop(0);

        // Remove any duplicates from merged result
        coordinates = coordinates.filter(point1 => {
          let duplicate = false;
          matches.forEach(data => {
            let point2 = data.point2;
            let d = new Vector2(...point1)
              .distanceTo(new Vector2(...point2));

            if (d < maxPointDistance) {
              duplicate = true;
            }
          });

          return !duplicate;
        });

        if (isPanoramicImage) {
          // Subtract image offset and convert to pixel
          let width = firstLabel.imageWidth;
          coordinates = coordinates.map(coord => {
            return {
              u: (coord[0] - (width/2)) % width,
              v: coord[1]
            };
          });
        }

        // Delete second label
        category.deleteLabel(mergeLabel.id);

        // Add points to first label
        firstLabel.coordinates = [];
        coordinates.forEach(coord => {
          firstLabel.addPoint(coord, false);
        });
        category.completeLabel(firstLabel.id);
        mergedLabels.add(mergeLabel.id);
      });
    });

    const remainingLabels = labelsToMerge.filter(label => {
      return mergedLabels.has(label.id) === false;
    });

    const numLabelMerged = mergedLabels.size;

    if (numLabelMerged > 0) {
      this.mergeTrainingLabels(category, remainingLabels);
    }
  }

  simplifyPanoramicLabels(category) {
    // Simplify new labels (panoramic)
    category.labels.forEach(label => {
      const isPanoramicImage = label.camera instanceof PanoramicImage;
      if (!isPanoramicImage) {
        return;
      }

      let coordinates = [...label.coords];
      coordinates = this.simplifyPolygon(coordinates);

      // Subtract image offset and convert to pixel
      let width = label.imageWidth;
      coordinates = coordinates.map(coord => {
        return {
          u: (coord[0] + width) % width,
          v: coord[1]
        };
      });

      label.coordinates = [];
      coordinates.forEach(coord => {
        label.addPoint(coord, false);
      });
      label.complete();
    });
  }

  /** @private */
  geometryWithBuffer(geom, bufferSize, divisor) {
    const JSTSReader = new jsts.io.GeoJSONReader();

    let geomJSTS = JSTSReader.read(geom);
    let buffer = geomJSTS.geometry.buffer(bufferSize);
    let points = buffer.getCoordinates().map(coord => [coord.x, coord.y]);
    points = this.simplifyPolygon(points, bufferSize/divisor);

    return points;
  }

  /** @private */
  checkForImageOverlap(label) {
    let hasOverlap = false;
    this.orthoLabels.visibleOrthos.forEach(ortho => {
      if (hasOverlap) {
        return;
      }

      let intersects = ortho.intersectAerialPoints(label.polygonPointsOnly);
      if (intersects) {
        hasOverlap = true;
      }
    });

    return hasOverlap;
  }

  /** @private */
  importLabelData(label, category, images, projection, progressData, isTrainingArea): Promise<any> {
    let labelAddTimeout = 5;
    let addedLabel;

    return new Promise(async resolve => {
      let cameras = this.viewer.cameraList;

      // Add new label
      let imageData = images[label.image_id];
      label.segmentation.forEach(points => {
        // Convert points to 2d array and simplify geometry
        points = coordinatesToArray(points);

        // Make sure enough points exist
        let minPoints = 3;
        if (label.is_linestring) {
          minPoints = 2;
        } else if (label.is_point) {
          minPoints = 1;
        }

        if (points.length < minPoints) {
          progressData.skippedLabels += 1;
          return;
        }

        if (this.activeLabeler instanceof AerialLabelTools) {

          if (label.is_linestring) {
            // Convert linestring to buffered polygon
            let lineGeom = turfLineString(points);
            points = this.geometryWithBuffer(lineGeom, label.buffer_size, 4);
          } else if (label.is_point) {
            let pointGeom = turfPoint(points[0]);
            points = this.geometryWithBuffer(pointGeom, label.buffer_size, 8);
          }

          // Create simplified version of polygon.
          if (!label.linework_data) {
            points = this.simplifyPolygon(points);
          }

          // Convert from image space to map coordinates
          points = imagePixelsToAerial(points, imageData, projection);
        } else if (this.activeLabeler instanceof SceneLabelTools) {
          // Planar images cant be immediately simplified
          let camera = cameras.getByName(imageData.file_name);
          if (camera && (camera instanceof PlanarImage)) {
            points = this.simplifyPolygon(points);
          }

          points = this.activeLabeler.pointsToPixels(points);
        }

        // Make sure enough points exist after simplification / pixel function
        if (points.length < 3) {
          progressData.skippedLabels += 1;
          return;
        }

        // Create label
        let newLabel = isTrainingArea
          ? this.addTrainingAreaLabel(false)
          : this.addLabel(category.id, false);

        newLabel.setScore(label.score);
        newLabel.setText(label.text, label.textScore);
        newLabel.setFindText(label.findText);

        if (this.activeLabeler instanceof SceneLabelTools) {
          // Camera is required for scene tools

          let camera = cameras.getByName(imageData.file_name);

          if (camera) {
            // Set camera label
            newLabel.setCamera(camera);
            newLabel.setKnownImageSize(
              imageData.width,
              imageData.height
            );
          } else {
            // Delete label and continue
            progressData.skippedLabels += 1;
            this.deleteLabel(newLabel.id);
            return;
          }
        }

        // Add points to new label
        points.forEach(point => {
          newLabel.addPoint(point, false);
        });


        newLabel.complete();
        addedLabel = newLabel;

      });

      // Update loading progress
      const {labelTotal, labelIndex} = progressData;
      progressData.progress = Math.min(100, (labelIndex+1)/labelTotal * 100);
      progressData.callback(progressData.progress);
      progressData.labelIndex += 1;

      setTimeout(() => {
        resolve(addedLabel);
      }, labelAddTimeout);
    });
  }

  addCategoryLabels(labels, category, images, projection, progressData, isTrainingArea): Promise<any> {
    return new Promise(async resolve => {
      let labelsToMerge = {};

      // Add all new labels
      for (let label of labels) {
        let addedLabel = await this.importLabelData(label, category, images,
          projection, progressData, isTrainingArea);

        if (addedLabel) {
          const mergeID = label.merge_id;
          if (mergeID) {
            let previous = labelsToMerge[mergeID];
            labelsToMerge[mergeID] = previous
              ? [...previous, addedLabel]
              : [addedLabel];
          }
        }
      }

      labelsToMerge = Object.keys(labelsToMerge)
        .map(x => labelsToMerge[x]);

      resolve(labelsToMerge);
    });
  }

  /** @private */
  checkLabelOverlap(newCategory, checkOverlap, progressData) {
    if (!checkOverlap) {
      return;
    }

    [...newCategory.labels].forEach(label => {
      let hasOverlap = this.checkForImageOverlap(label);
      if (!hasOverlap) {
        progressData.skippedLabels += 1;
        newCategory.deleteLabel(label.id);
      }
    });
  }

  /** @private */
  checkLabelInvalid(newCategory, progressData) {
    [...newCategory.labels].forEach(label => {
      if (!label.hasEnoughPoints) {
        progressData.skippedLabels += 1;
        newCategory.deleteLabel(label.id);
      };
    });
  }

  private clearTooltips() {
    clearAerialTooltip();
    clearViewerTooltip();
  }

  async loadImageLabelData(data, progressCallback, overwrite, checkOverlap) {
    if (overwrite) {
      this.deleteAllCategories();
    }

    let projection = data.projection;
    let categories = data.categories;
    let labels = data.annotations;
    let hasTrainingArea = data.hasOwnProperty('trainingAreas');

    // Check for valid projection string
    let validProjection = isValidProjection(projection.string);
    if (!validProjection) {
      toast.error("Invalid projection string");
      return;
    }

    let images = {};
    data.images.forEach(image => {
      images[image.id] = image;
    });

    // Calculate total combined labels
    let numLabelsTotal = 0;
    data.annotations.forEach(label => {
      numLabelsTotal += label.segmentation.length;
    });

    let progressData = {
      progress: 0,
      labelTotal: numLabelsTotal,
      labelIndex: 0,
      skippedLabels: 0,
      callback: progressCallback
    };

    // Stop category UI update
    this.setUpdateState(false);

    if (hasTrainingArea) {
      let trainingAreaCategory = this.addTrainingArea();

      let areasToMerge = await this.addCategoryLabels(
        data.trainingAreas, trainingAreaCategory, images,
        projection, progressData, true);

      // Merge training data was was previously connected
      areasToMerge.forEach(mergeList => {
        this.mergeTrainingLabels(trainingAreaCategory, mergeList);
      });

      // Complete all labels
      trainingAreaCategory.labels.forEach(label => {
        trainingAreaCategory.completeLabel(label.id);
      });
    }

    for (let category of categories) {
      let newLabels = labels.filter(x => x.category_id === category.id);

      let currentCategory = this.categories.find(x => x.name === category.name);

      // Add new category (existing or current)
      let newCategory = currentCategory
        ? currentCategory
        : this.addCategory(category.name);

      let labelsToMerge = await this.addCategoryLabels(
        newLabels, newCategory, images, projection,
        progressData, false
      );

      // Merge training data was was previously connected
      labelsToMerge.forEach(mergeList => {
        this.mergeTrainingLabels(newCategory, mergeList);
      });

      // Check for overlap between label and image
      this.checkLabelOverlap(newCategory, checkOverlap, progressData);

      // Simplify panoramic labels
      this.simplifyPanoramicLabels(newCategory);

      // Remove any labels that are invalid now
      this.checkLabelInvalid(newCategory, progressData);

      // Complete all labels
      newCategory.labels.forEach(label => {
        newCategory.completeLabel(label.id);
      });

      // Remove category if nothing is left
      if (newCategory.labels.length === 0) {
        console.log(`Delete empty category: ${newCategory.name}`);
        this.deleteCategory(newCategory.id);
      } else {
        console.log(`Import labels for category: ${newCategory.name}`);
      }
    };

    // Set labels to drawable
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.setDrawable(true);
      });
    });

    this.trainingArea?.labels.forEach(label => {
      label.setDrawable(true);
    });

    // Draw all new labels
    this.activeLabeler.drawAll();

    // Categories can be updated in UI
    this.setUpdateState(true);

    if (progressData.skippedLabels > 0) {
      toast.error(`Skipped ${progressData.skippedLabels} labels`);
    }

    setTimeout(() => {
      this.setCategories();
      this.clearTooltips();
      progressCallback(0);
    }, 500);
  }

  update(changeDetector) {
    this.sceneLabels.update(changeDetector);
  }

  labelObject() {
    let categories = this.categories.map((category, categoryID) => {
      let [r, g, b] = textToColorArray(category.name, 0);
      let color = [r, g, b];

      return {
        supercategory: category.name,
        id: categoryID,
        name: category.name,
        color: color
      } as CocoCategory;
    });

    let annotations = this.activeLabeler.getLabelAnnotations();
    let trainingAreas = this.activeLabeler.getTrainingAnnotations();

    let jsonObj = {
      type: this.activeLabeler.type,
      categories: categories,
      projection: LocalScene.dataProjection,
      images: this.activeLabeler.jsonImageObj(),
      annotations: annotations,
      ...(trainingAreas.length > 0) && {trainingAreas},
    };

    return jsonObj;
  }
}

export const sectionsFromIntersection = (intersection, removeDuplicate=true) => {
  let sections = [];

  if (!intersection) {
    return sections;
  }

  let geometry = intersection.geometry;
  geometry.coordinates.forEach(intersectPoints => {
    if (geometry.type === "MultiPolygon") {
      intersectPoints = intersectPoints[0];
    }

    if (removeDuplicate) {
      intersectPoints.pop(0);
    }
    sections.push(intersectPoints);
  });

  return sections;
};

export const annotationFromPolygon = (polygon, labelIndex, categoryID, imageID, mergeID, score=1.0, text='', textScore=0.0, findText=false) => {
  let segment = [...polygon].flat();
  let allX = polygon.map(x => x[0]);
  let allY = polygon.map(x => x[1]);

  let boundBox = [
    Math.min(...allX),
    Math.min(...allY),
    Math.max(...allX) - Math.min(...allX),
    Math.max(...allY) - Math.min(...allY)
  ].map(x => Math.round(x));

  let area = areaFromPolygon(polygon);
  let findTextNum = findText ? 1 : 0;

  let annotation = {
    segmentation: [segment],
    iscrowd: 0,
    image_id: imageID,
    bbox: boundBox,
    category_id: categoryID,
    id: labelIndex,
    area: area,
    score:score,
    text: text,
    textScore:textScore,
    findText: findTextNum
  } as CocoAnnotation;

  if (mergeID !== null) {
    annotation.merge_id = mergeID;
  }

  return annotation;
};

export const areaFromPolygon = (polygon) => {
  let contour = polygon.map(x => new Vector2(...x));
  return Math.abs(ShapeUtils.area(contour));
};

const minDistanceIdx = (coordArray, point) => {
  let shortestDistance = Infinity;
  let closestPoint = new Vector3();
  let idxCoord;

  for (let idx = 0; idx < coordArray.length; idx++) {
    let endIdx = (idx === coordArray.length - 1) ? 0 : idx + 1;
    let lineSegment = createSegment(coordArray[idx], coordArray[endIdx]);
    lineSegment.closestPointToPoint(point, true, closestPoint);
    let distance = point.distanceTo(closestPoint);
    if (Math.abs(distance) < Math.abs(shortestDistance)) {
      shortestDistance = distance;
      idxCoord = idx;
    }
  }

  return idxCoord;
};

const createSegment = (P1, P2) => {
  let startPoint = new Vector3(P1[0], P1[1]);
  let endPoint = new Vector3(P2[0], P2[1]);
  return new Line3(startPoint, endPoint);
};